import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useEventsStore } from '../store';
import type { ScheduledEvent } from './type';
import type { UseEventsHook } from './use-events';
import { useEvents } from './use-events';

const getValidFavoriteIds = (
  events: ScheduledEvent[],
  eventIDs: Set<ScheduledEvent['eid']>
): Set<ScheduledEvent['eid']> => {
  const eventMap: Map<string, ScheduledEvent> = new Map(
    events.map((v) => [v.eid, v])
  );

  const set = new Set(eventIDs);

  set.forEach(eventId => {
    const event = eventMap.get(eventId);
    const isPastEvent = event && !event.isLive && DateTime.fromISO(event.endTime) < DateTime.now();

    if (isPastEvent) {
      set.delete(eventId);
    }
  });

  return set;
};

export const useFavoriteEvents: UseEventsHook = (filters) => {
  const { favoriteEvents, setFavoriteEvents } = useEventsStore((v) => ({
    favoriteEvents: v.favoriteEvents,
    setFavoriteEvents: v.setFavoriteEvents
  }));

  const { events: events_, ...result } = useEvents({ ...filters, eventIds: [...favoriteEvents] }, {});

  const events = useMemo((): typeof events_ => {
    if (!events_) {
      return undefined;
    }

    const acceptedEvents = getValidFavoriteIds(events_, favoriteEvents);

    setFavoriteEvents([...acceptedEvents]);

    return acceptedEvents.size ? events_.filter((v) => acceptedEvents.has(v.eid)) : [];
  }, [events_]);

  return { ...result, events };
};
